import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import supabase from "../supabaseClient";
import "./Gallery.css";

export default function Gallery() {
  const [sliders, setSliders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("images");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const pageSize = 12; // Changed to 12 for better grid layout
  const [totalImages, setTotalImages] = useState(0);

  useEffect(() => {
    fetchSliders();
    fetchGalleryVideos();
    fetchGalleryImages(currentPage);
  }, [currentPage]);

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("sliders")
        .select("id, image_url, created_at")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Sliders fetch error:", error);
      } else {
        setSliders(data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sliders:", error);
      setLoading(false);
    }
  };

  const fetchGalleryVideos = async () => {
    try {
      const { data: videosData, error: videosError } = await supabase
        .from("gallery_videos")
        .select("id, video_url, company_id, title, description, thumbnail_url")
        .order("created_at", { ascending: false });

      if (videosError) {
        console.error("Gallery videos fetch error:", videosError);
      } else {
        setVideos(videosData || []);
      }
    } catch (error) {
      console.error("Error fetching gallery videos:", error);
    }
  };

  const fetchGalleryImages = async (page = 1) => {
    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { count } = await supabase
        .from("gallery_images")
        .select("*", { count: "exact", head: true });
      setTotalImages(count || 0);

      const { data, error } = await supabase
        .from("gallery_images")
        .select("id, image_url, title, description, created_at")
        .eq("status", true)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) {
        console.error("Gallery images fetch error:", error);
      } else {
        setGalleryImages(data || []);
      }
    } catch (error) {
      console.error("Error fetching gallery images:", error);
    }
  };

  const extractYouTubeVideoId = (url) => {
    if (!url) return null;

    const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (shortsMatch) return shortsMatch[1];

    const regExp =
      /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]{11}).*/;
    const match = url.match(regExp);
    return match && match[1].length === 11 ? match[1] : null;
  };

  const openImageModal = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const allImages = [...sliders, ...galleryImages];
    const newIndex =
      direction === "next"
        ? (currentImageIndex + 1) % allImages.length
        : (currentImageIndex - 1 + allImages.length) % allImages.length;

    setCurrentImageIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };

  const totalPages = Math.ceil(totalImages / pageSize);

  if (loading) {
    return (
      <div className="gallery-loading">
        <div className="loading-spinner"></div>
        <h3>Loading gallery...</h3>
      </div>
    );
  }

  return (
    <div className="gallery-page">
      {/* Navigation */}
      <nav className="gallery-navbar">
        <div className="nav-container">
          <Link to="/" className="logo" alt="Back to Home">
            {"<"}{" "}
            <img
              src="/assets/logovg.png"
              alt="Vachana Group Logo"
              className="logo-image"
            />
          </Link>
          <div className="nav-links">
            <button
              className={activeTab === "images" ? "active" : ""}
              onClick={() => setActiveTab("images")}
            >
              Images
            </button>
            <button
              className={activeTab === "videos" ? "active" : ""}
              onClick={() => setActiveTab("videos")}
            >
              Videos
            </button>
            
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="gallery-hero">
        <div className="gallery-hero-background">
          <div className="angled-overlay"></div>
        </div>

        <div className="gallery-hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Our Gallery</h1>
            <p className="hero-subtitle">
              Explore our projects, achievements, and behind-the-scenes moments
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="gallery-content">
        <div className="container">
          {/* Tab Navigation */}
          <div className="gallery-tabs">
            <button
              className={`tab-btn ${activeTab === "images" ? "active" : ""}`}
              onClick={() => setActiveTab("images")}
            >
              <span className="tab-icon">🖼️</span>
              Images
            </button>
            <button
              className={`tab-btn ${activeTab === "videos" ? "active" : ""}`}
              onClick={() => setActiveTab("videos")}
            >
              <span className="tab-icon">🎥</span>
              Videos
            </button>
          </div>

          {/* Images Tab */}
          {activeTab === "images" && (
            <div className="images-section">
              <div className="section-header">
                <h2>Featured Images</h2>
                <p>Browse through our gallery of memorable moments</p>
              </div>

              {/* Featured Sliders */}
              {sliders.length > 0 && (
                <div className="featured-images">
                  <h3>Featured Gallery</h3>
                  <div className="featured-grid">
                    {sliders.slice(0, 4).map((slider, index) => (
                      <div
                        key={slider.id}
                        className="featured-image-card"
                        onClick={() => openImageModal(slider, index)}
                      >
                        <div className="image-container">
                          <img
                            src={slider.image_url}
                            alt={`Featured ${index + 1}`}
                          />
                          <div className="image-overlay">
                            <div className="overlay-content">
                              <span className="view-icon">👁️</span>
                              <p>View Full Size</p>
                            </div>
                          </div>
                        </div>
                        <div className="image-badge">Featured</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery Images Grid */}
              {galleryImages.length > 0 ? (
                <div className="gallery-images">
                  <h3>Gallery Collection</h3>
                  <div className="images-grid">
                    {galleryImages.map((image, index) => (
                      <div
                        key={image.id}
                        className="gallery-image-card"
                        onClick={() =>
                          openImageModal(image, sliders.length + index)
                        }
                      >
                        <div className="image-container">
                          <img
                            src={image.image_url}
                            alt={image.title || "Gallery Image"}
                          />
                          <div className="image-overlay">
                            <div className="overlay-content">
                              <span className="view-icon">👁️</span>
                              <p>View Full Size</p>
                            </div>
                          </div>
                        </div>
                        {image.title && (
                          <div className="image-info">
                            <h4>{image.title}</h4>
                            {image.description && <p>{image.description}</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ← Previous
                      </button>

                      <div className="page-numbers">
                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <button
                            key={page}
                            className={`page-btn ${
                              page === currentPage ? "active" : ""
                            }`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        ))}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-content">
                    <span className="empty-icon">📸</span>
                    <h3>No Images Available</h3>
                    <p>Gallery images will appear here once they're added.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === "videos" && (
            <div className="videos-section">
              <div className="section-header">
                <h2>Video Gallery</h2>
                <p>Watch our project videos and highlights</p>
              </div>

              {videos.length > 0 ? (
                <div className="videos-grid">
                  {videos.map((video) => {
                    const videoId = extractYouTubeVideoId(video.video_url);
                    const thumbnailUrl = videoId
                      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                      : video.thumbnail_url;

                    return (
                      <div key={video.id} className="video-card">
                        <div className="video-container">
                          <div className="video-thumbnail">
                            <img
                              src={thumbnailUrl}
                              alt={video.title || "Video"}
                            />
                            <div className="play-overlay">
                              <div className="play-button">▶️</div>
                            </div>
                          </div>
                          <iframe
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={video.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            style={{ display: "none" }}
                          />
                        </div>

                        <div className="video-info">
                          <h4>{video.title || "Untitled Video"}</h4>
                          {video.description && <p>{video.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-content">
                    <span className="empty-icon">🎥</span>
                    <h3>No Videos Available</h3>
                    <p>Video content will appear here once it's added.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <button
              className="modal-nav prev"
              onClick={() => navigateImage("prev")}
            >
              ‹
            </button>
            <button
              className="modal-nav next"
              onClick={() => navigateImage("next")}
            >
              ›
            </button>

            <div className="modal-image">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title || "Gallery Image"}
              />
            </div>

            {(selectedImage.title || selectedImage.description) && (
              <div className="modal-info">
                {selectedImage.title && <h3>{selectedImage.title}</h3>}
                {selectedImage.description && (
                  <p>{selectedImage.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
